/* eslint-disable anti-slop/require-safety-comment-for-type-assertion, anti-slop/no-object-parameters, anti-slop/no-known-value-widening -- verbatim restore of the pre-FA-3036 data dashboard, served while data_dashboard_v2_enabled is off */
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { DURATION_PREFIX, chartColors } from './constants';
import {
  FORTA_BLUE,
  FORTA_GRAY4,
  FORTA_GRAY3,
  FORTA_PINK,
  FORTA_ORANGE,
} from '../../../utils/colors';
import { TARGET_STAGE_LOG_PHASE_CHANGE_TYPE } from '../../../pages/constants';
import { TPGoalDataCollectionMethod } from '../treatment-plan/goals/common/constants/general';
import { getChartLabelByDCM } from '../../../pages/utils';

import type { DurationChartPlotMethod } from '../../../pages/constants';
import type { ITargetPhaseChange, IDashboardTarget } from '../../../utils/types';

interface IDataChartProps {
  type?: string;
  durationUnit?: string;
  rateUnit?: string;
  durationChartPlotMethod: DurationChartPlotMethod;
  selectedTargets: IDashboardTarget[];
  chartData: { [key: string]: string }[];
  startDate: string | null;
  endDate: string | null;
  filteredPhaseChanges: ITargetPhaseChange[];
  showBaselineDataPoint: boolean;
}

const CustomTooltip = ({ active, payload, label, durationChartPlotMethod }: any) => {
  if (active && payload && payload.length) {
    const tooltipData = { ...payload[0].payload };
    const name = payload[0].unit;
    const isPhaseChange = name === 'phase-change-data-type';
    const isBaselineDatapoint = name === 'baseline';
    const isPercentageType = name === TPGoalDataCollectionMethod.PERCENTAGE;
    const durationsOfTargets: { [key: string]: string } = {};
    for (const key in tooltipData) {
      if (key.includes(DURATION_PREFIX)) {
        durationsOfTargets[key.replace(DURATION_PREFIX, '')] = tooltipData[key];
        delete tooltipData[key];
      }
    }

    if (isBaselineDatapoint) {
      return (
        <Box
          sx={{
            background: 'white',
            borderRadius: '4px',
            padding: 2,
            borderWidth: 1,
            borderColor: FORTA_ORANGE,
            borderStyle: 'solid',
            maxWidth: '350px',
          }}
        >
          <Typography>{`Date: ${moment.unix(label).format('MM/DD/YY')}`}</Typography>
          {Object.entries(tooltipData).map(
            (datum: any, index: number) =>
              datum[0] !== 'date' && (
                <Box
                  key={index}
                  textAlign={'left'}
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: '4px',
                    border: `1px solid ${FORTA_ORANGE}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: FORTA_ORANGE,
                      fontWeight: 700,
                    }}
                  >
                    {datum[0]}
                  </Typography>
                  <Typography>{`Baseline level: ${datum[1]}`}</Typography>
                </Box>
              ),
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          background: 'white',
          borderRadius: '4px',
          padding: 2,
          borderWidth: 1,
          borderColor: isPhaseChange
            ? TARGET_STAGE_LOG_PHASE_CHANGE_TYPE
              ? FORTA_PINK
              : FORTA_BLUE.primary
            : chartColors[0],
          borderStyle: 'solid',
          maxWidth: '350px',
        }}
      >
        <Typography>{`Date: ${moment.unix(label).format('MM/DD/YY')}`}</Typography>
        {!isBaselineDatapoint && (
          <Typography
            color={
              isPhaseChange
                ? TARGET_STAGE_LOG_PHASE_CHANGE_TYPE
                  ? FORTA_PINK
                  : FORTA_BLUE.primary
                : chartColors[0]
            }
          >
            {isPhaseChange ? 'Phase change:' : 'Target(s):'}
          </Typography>
        )}
        {Object.entries(tooltipData).map(
          (datum: any, index: number) =>
            datum[0] !== 'date' && (
              <Box
                key={index}
                textAlign={'left'}
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: '4px',
                  border: `1px solid ${chartColors[(index - 1) % chartColors.length]}`,
                }}
              >
                <Typography
                  sx={{
                    color: isPhaseChange
                      ? TARGET_STAGE_LOG_PHASE_CHANGE_TYPE
                        ? FORTA_PINK
                        : FORTA_BLUE.primary
                      : chartColors[(index - 1) % chartColors.length],
                  }}
                >
                  {`Title: ${datum[0]}`}
                </Typography>
                {!isPhaseChange ? (
                  <Typography sx={{ color: chartColors[(index - 1) % chartColors.length] }}>
                    {`${
                      isPercentageType
                        ? 'Success'
                        : getChartLabelByDCM(name, durationChartPlotMethod)
                    }: ${datum[1]}${isPercentageType ? '%' : ''}`}
                  </Typography>
                ) : null}
                {!isPhaseChange && durationsOfTargets[datum[0]] ? (
                  <Box>
                    <Typography
                      display="inline"
                      sx={{ color: chartColors[(index - 1) % chartColors.length] }}
                    >
                      {`Durations: ${durationsOfTargets[datum[0]]?.split('--')?.[0] ?? ''}`}
                    </Typography>
                    <Typography
                      fontWeight={700}
                      display="inline"
                      sx={{ color: chartColors[(index - 1) % chartColors.length] }}
                    >{`${durationsOfTargets[datum[0]]?.split('--')?.[1] ?? ''}`}</Typography>
                  </Box>
                ) : null}
              </Box>
            ),
        )}
      </Box>
    );
  }

  return null;
};

function DataChartLegacy({
  endDate,
  startDate,
  durationUnit,
  rateUnit,
  durationChartPlotMethod,
  filteredPhaseChanges = [],
  selectedTargets = [],
  chartData = [],
  showBaselineDataPoint,
}: IDataChartProps) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        width={400}
        data={chartData}
        margin={{
          top: 10,
          right: 40,
          left: 0,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          allowDuplicatedCategory={false}
          allowDataOverflow
          type={'number'}
          domain={[moment(startDate).unix(), moment(endDate).unix()]}
          tickFormatter={(unixTime) => moment.unix(unixTime).format('MM/DD/YY')}
          label={{
            value: 'Date',
            stroke: FORTA_BLUE.primary,
            fontWeight: 200,
            dy: 45,
          }}
        />
        <YAxis
          type="number"
          domain={filteredPhaseChanges.length > 0 ? [0, 'dataMax'] : [0, 'dataMax + 3']}
          tickFormatter={(value) => `${Math.round(value)}`}
          label={{
            value:
              selectedTargets[0]?.type === TPGoalDataCollectionMethod.PERCENTAGE
                ? '%'
                : selectedTargets[0]?.type === TPGoalDataCollectionMethod.RATE
                  ? `Rate per ${rateUnit?.substring(0, rateUnit.length - 1)}`
                  : selectedTargets[0]?.type === TPGoalDataCollectionMethod.DURATION
                    ? `Duration in ${durationUnit}`
                    : 'Instances',
            angle: selectedTargets[0]?.type === 'percent' ? 0 : -90,
            position: 'insideLeft',
            stroke: FORTA_BLUE.primary,
            fontWeight: 200,
          }}
        />
        <Tooltip
          content={(props) => (
            <CustomTooltip {...props} durationChartPlotMethod={durationChartPlotMethod} />
          )}
          cursor={{ visibility: 'default', stroke: FORTA_GRAY3, strokeWidth: 1 }}
        />
        <Legend />
        {selectedTargets.map((selectedTarget: any, index: number) => (
          <Line
            connectNulls
            key={selectedTarget?.id}
            type="monotone"
            stroke={chartColors[index % chartColors.length]}
            strokeWidth={1.5}
            activeDot={{ r: 8 }}
            unit={selectedTarget?.type}
            name={selectedTarget?.label}
            dataKey={selectedTarget?.label}
          />
        ))}

        {/* Baseline data point */}
        {showBaselineDataPoint && (
          <Line
            connectNulls
            key={'baseline-value'}
            type="linear"
            dataKey={'Baseline'}
            stroke={FORTA_ORANGE}
            strokeWidth={4}
            activeDot={{ r: 10 }}
            dot={{
              r: 6,
              fill: FORTA_ORANGE,
              stroke: FORTA_BLUE.primary,
              strokeWidth: 1,
            }}
            name="baseline"
            unit="baseline"
          />
        )}

        {/* Trend line */}
        {selectedTargets.length === 1 && (
          <Line
            connectNulls
            type="linear"
            dataKey={`${selectedTargets[0]?.label}_trend`}
            stroke={FORTA_GRAY4}
            strokeWidth={1.5}
            strokeDasharray="5 5"
            dot={false}
            activeDot={false}
            name="Trend"
            unit="Trend"
            legendType="line"
          />
        )}

        {/* Phase change lines */}
        {selectedTargets.length === 1 &&
          filteredPhaseChanges?.length > 0 &&
          filteredPhaseChanges.map((phase: ITargetPhaseChange) => (
            <Line
              key={phase.id}
              dataKey={phase.title}
              strokeDasharray="6 2"
              stroke={
                phase.type === TARGET_STAGE_LOG_PHASE_CHANGE_TYPE ? FORTA_PINK : FORTA_BLUE.primary
              }
              dot={false}
              unit="phase-change-data-type"
              name="phase-change-data-type"
              strokeWidth={1.5}
              activeDot={{ r: 8 }}
              legendType="none"
            />
          ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default DataChartLegacy;
